/**
 * sticky-scroll-activation.js
 *
 * Scroll-activation core: flip a class on a bar once a trigger element has
 * scrolled up under the sticky header. That is the ONLY thing this file does.
 * It owns no markup, no buttons, no cart behaviour - CSS decides what the
 * active class looks like.
 *
 * WHY IT EXISTS. This logic was written once inside assets/pdp-sticky-bar.js
 * (the native PDP bar) and was about to be written a second time for the
 * configurator's actions bar. The measurement rules below are not obvious -
 * each one is a bug that was already paid for once - so a second copy would
 * have been a second place for them to rot. pdp-sticky-bar.js still carries its
 * own inline copy today and is scheduled to adopt this module in a follow-up;
 * do not add a THIRD implementation in the meantime.
 *
 * USAGE (declarative - no consumer JS needed):
 *
 *   <div class="my-bar"
 *        data-sticky-activate="my-bar--stuck"
 *        data-sticky-trigger=".my-in-flow-thing">
 *
 * USAGE (programmatic):
 *
 *   window.MDStickyActivation.create({ bar, trigger, activeClass, onToggle });
 *
 * ---------------------------------------------------------------------------
 * THE FOUR MEASUREMENT RULES. Each is load-bearing.
 *
 * 1. HEADER HEIGHT comes from getBoundingClientRect().bottom, clamped at 0 -
 *    never offsetHeight. `.sticky-header` hides on downward scroll via
 *    `top: calc(-1 * var(--header-height))`. offsetHeight still reports the
 *    full box while it is parked off-screen, so it overshoots by the whole
 *    header (~56-72px on mobile) exactly when the header is hidden.
 *
 * 2. TRIGGER POSITION comes from an offsetTop chain, not
 *    getBoundingClientRect().top. On iOS Safari the toolbar animation changes
 *    the VISUAL viewport without changing window.scrollY; a rect-based read
 *    drifts against the scrollY it is compared to. An offsetTop chain is
 *    layout-based, so both sides of the comparison use the same coordinate
 *    space.
 *
 * 3. THE SENTINEL IS `display: block`. base.css carries `div:empty
 *    { display: none }`; a zero-height empty div inherits it, offsetParent
 *    goes null, and the offsetTop chain silently returns 0 - which reads as
 *    "trigger is at the very top", i.e. permanently active.
 *
 * 4. UPDATES FREEZE WHILE THE BODY IS SCROLL-LOCKED. The iOS-safe lock
 *    (`body { position: fixed; top: -Npx }`) drops window.scrollY to 0 and
 *    fires a scroll event. Without the guard the bar reads that artifact as
 *    "scrolled to top" and tears itself down the moment any overlay opens.
 *    See the Quick Config Advanced drawer incident in CLAUDE.md.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  // Loaded by the layout on most pages and by sections/md-configurator.liquid
  // on the configurator; a template can end up with both tags. Run once.
  if (window.MDStickyActivation) return;

  var DEFAULT_FREEZE_CLASS = 'qc-advanced-open';

  /** Rule 2: layout-based document offset, immune to iOS visual-viewport drift. */
  function getDocTop(el) {
    var top = 0;
    var node = el;
    while (node) {
      top += node.offsetTop || 0;
      node = node.offsetParent;
    }
    return top;
  }

  /** Rule 1: visible header height, 0 while the header is parked off-screen. */
  function measureHeaderHeight() {
    var header =
      document.querySelector('.sticky-header') ||
      document.querySelector('.header-wrapper') ||
      document.getElementById('shopify-section-header-group');
    if (!header) return 0;
    return Math.max(0, header.getBoundingClientRect().bottom);
  }

  /* -------------------------------------------------------------------------
   * PINNED SITE-HEADER INTRUSION  ->  --md-pinned-header-height
   * -------------------------------------------------------------------------
   * A SECOND, DELIBERATELY DIFFERENT QUANTITY from measureHeaderHeight() above.
   * Do not merge them:
   *
   *   measureHeaderHeight()  rect.bottom clamped at 0. SCROLL-DEPENDENT by
   *                          design - "how much header is on screen right now",
   *                          which is what an activation threshold wants.
   *   pinned intrusion       the header's sticky/fixed INSET plus its height.
   *                          SCROLL-INDEPENDENT - "how far down the viewport
   *                          will this header sit once it is pinned", which is
   *                          what a sticky `top` offset wants.
   *
   * Feeding the scroll-dependent number into a sticky `top` would make the
   * offset element slide as the page scrolls: on desktop the header is
   * `top: auto` and simply scrolls away, so rect.bottom runs 122 -> 0 and the
   * consumer would jump by a whole header height mid-scroll.
   *
   * WHY THIS EXISTS AT ALL, i.e. why not just use `--header-height`.
   * That variable is NEVER SET on this store. sections/header.liquid renders
   * `<div sticky-header>` - an ATTRIBUTE - while registering `sticky-header` as
   * a TAG, so the StickyHeader element never upgrades and its setHeaderHeight()
   * never runs. Measured on production (theme 132070506541) 2026-09-15:
   * getComputedStyle(documentElement).getPropertyValue('--header-height') is ''
   * at every viewport on the PDP. Every `var(--header-height, 0px)` in the theme
   * therefore silently resolves to 0px, so "offset by --header-height" reads
   * like a fix and changes nothing. That upgrade bug is tracked separately and
   * is explicitly not to be fixed drive-by - it touches every page on the site.
   *
   * Meanwhile the header IS pinned on phones: a third-party app adds
   * `M_SW_Phone_Support_sticky-nav` at runtime (absent from server HTML, styled
   * from a CORS-opaque app stylesheet) giving `position: sticky; top: 0;
   * z-index: 999999`. Measured heights - 49px at 320/360/375, 49.8 at 390,
   * 51.1 at 412, 52.1 at 430, 54.8 at 480, 57.4 at 540 - and it stops pinning
   * between 550 and 560px, which is not a theme breakpoint. Neither the value
   * nor the cut-off is a constant we own, which is exactly why this is measured
   * instead of hard-coded.
   *
   * Consumers read `var(--md-pinned-header-height, 0px)`. If this never runs,
   * or the header never pins, they get 0px - today's layout, unchanged.
   * ------------------------------------------------------------------------- */
  var PINNED_HEADER_PROP = '--md-pinned-header-height';
  var HEADER_CHROME_SELECTOR = '.shopify-section-group-header-group';
  var lastPinnedHeader = null;

  // sections/header.liquid's StickyHeader toggles these on the header group
  // element; base.css gives them their `top` (hidden: -header-height,
  // hidden.menu-open: 0) and `.section-header.animate` slides with
  // `transition: top`.
  var HEADER_HIDDEN_CLASS = 'shopify-section-header-hidden';
  var HEADER_MENU_OPEN_CLASS = 'menu-open';
  // One frame longer than base.css's 0.15s slide, plus slack: the backstop
  // re-read for when neither transitionend nor transitioncancel arrives.
  var PINNED_HEADER_SETTLE_MS = 200;

  /**
   * The `top` a running `top` transition is heading to, in px.
   *   undefined  no `top` transition is running: the computed value is final.
   *   null       one is running but its end value is unreadable.
   */
  function runningTopTarget(el) {
    if (typeof el.getAnimations !== 'function') return undefined;
    var anims;
    try {
      anims = el.getAnimations();
    } catch (_) {
      return undefined;
    }
    for (var i = 0; i < anims.length; i++) {
      var a = anims[i];
      if (a.transitionProperty !== 'top') continue;
      try {
        var frames = a.effect && a.effect.getKeyframes();
        var end = frames && frames.length && frames[frames.length - 1].top;
        var px = parseFloat(end);
        return isFinite(px) ? px : null;
      } catch (_) {
        return null;
      }
    }
    return undefined;
  }

  /**
   * How far into the viewport one element reaches once pinned. 0 if it never pins.
   *
   * TARGET STATE, NOT THE ANIMATED ONE. The theme header slides via `top`
   * (base.css `.section-header.animate { transition: top 0.15s }`) and this runs
   * from a MutationObserver on its class, i.e. at the START of the slide. The
   * computed `top` there is where the slide starts: 0 intrusion right after a
   * reveal (the header then covers 0-49.8px over the chip and its cart icon eats
   * taps on the 3D toggle), or a partial 22-36px when a second scroll lands
   * mid-slide. So while a `top` transition runs, its END keyframe is the inset.
   *
   * Why not derive it from the classes alone: measured on staging 2026-09-30
   * with an iPhone UA, the third-party `M_SW_Phone_Support_sticky-nav` rule
   * (`top: 0`) beats `.shopify-section-header-hidden`, so the header sits at
   * 0-49.8px WITH the hidden class on and no transition runs. A class-derived 0
   * there would park the chip under the header for good. The classes are used
   * only when a transition is running (so they are the rule that is winning)
   * and its end value cannot be read.
   */
  function elementPinnedIntrusion(el) {
    var cs = window.getComputedStyle(el);
    if (cs.position !== 'sticky' && cs.position !== 'fixed') return 0;
    var height = el.getBoundingClientRect().height;
    if (!height) return 0;
    var inset = runningTopTarget(el);
    if (inset === null) {
      var cl = el.classList;
      var parked =
        cl.contains(HEADER_HIDDEN_CLASS) &&
        !cl.contains(HEADER_MENU_OPEN_CLASS);
      // base.css: hidden -> -height, revealed / held open by the menu -> 0.
      return parked ? 0 : height;
    }
    if (inset === undefined) {
      // `top: auto` computes to the string "auto" -> NaN. A sticky box with no
      // top inset never pins, it just scrolls away with the document (desktop).
      inset = parseFloat(cs.top);
      if (!isFinite(inset)) return 0;
    }
    // A negative inset (parked off-screen, or heading there) correctly yields 0
    // rather than a phantom offset.
    return Math.max(0, inset + height);
  }

  /**
   * Tallest intrusion across the header group (announcement bar + header).
   *
   * MAX, not SUM — and that is correct even when the chrome STACKS. Properly
   * stacked sticky chrome gives each box a different inset (Dawn pins a sticky
   * announcement bar at `top: 0` and then pushes the header to
   * `top: <announcement height>`), so the lowest box's own `inset + height`
   * already spans everything above it. Summing would double-count.
   */
  function measurePinnedHeaderHeight() {
    var els = document.querySelectorAll(HEADER_CHROME_SELECTOR);
    var max = 0;
    for (var i = 0; i < els.length; i++) {
      var value = elementPinnedIntrusion(els[i]);
      if (value > max) max = value;
    }
    return max;
  }

  function publishPinnedHeaderHeight() {
    var next = Math.round(measurePinnedHeaderHeight() * 100) / 100;
    // Writing a custom property on :root invalidates style for the whole
    // document, so never write one that did not change.
    if (next === lastPinnedHeader) return next;
    lastPinnedHeader = next;
    document.documentElement.style.setProperty(PINNED_HEADER_PROP, next + 'px');
    return next;
  }

  function watchPinnedHeader() {
    publishPinnedHeaderHeight();

    window.addEventListener('resize', publishPinnedHeaderHeight, {
      passive: true,
    });
    window.addEventListener('orientationchange', publishPinnedHeaderHeight, {
      passive: true,
    });
    // Logo and webfont loads settle after DOMContentLoaded and change height.
    window.addEventListener('load', publishPinnedHeaderHeight);
    document.body.addEventListener('header:update', publishPinnedHeaderHeight);

    var els = document.querySelectorAll(HEADER_CHROME_SELECTOR);
    if (!els.length) return;

    var i;
    // Height changes: logo decode, announcement bar opening/closing.
    if (window.ResizeObserver) {
      // Construct off `window`, matching the guard directly above. Same object
      // in a browser; keeping them consistent means a harness that swaps the
      // constructor cannot end up guarding on one and building the other.
      var ro = new window.ResizeObserver(publishPinnedHeaderHeight);
      for (i = 0; i < els.length; i++) ro.observe(els[i]);
    }
    // The third-party sticky-nav class lands AFTER first paint and flips
    // `position`/`top` WITHOUT changing height - invisible to a ResizeObserver.
    // Observed attributes only; the write target is :root, so no feedback loop.
    if (window.MutationObserver) {
      var mo = new window.MutationObserver(onHeaderMutation);
      for (i = 0; i < els.length; i++) {
        mo.observe(els[i], {
          attributes: true,
          attributeFilter: ['class', 'style'],
        });
      }
    }
    // Re-read when the header's own `top` slide finishes or is cut short.
    // transitionend BUBBLES: an announcement-bar child, a Klaviyo/FigPii
    // injection or the header's own menus all finish transitions inside this
    // element, so only the element's own `top` counts (known-footguns.md,
    // "transitionend + {once: true} Consumed by Bubbled Child Events").
    for (i = 0; i < els.length; i++) {
      els[i].addEventListener('transitionend', onHeaderTopTransition);
      els[i].addEventListener('transitioncancel', onHeaderTopTransition);
    }
  }

  function onHeaderTopTransition(e) {
    if (e.target !== e.currentTarget || e.propertyName !== 'top') return;
    publishPinnedHeaderHeight();
  }

  var pinnedSettleTimer = null;

  /** Class/style flip: publish now, and once more after the slide should be over. */
  function onHeaderMutation() {
    publishPinnedHeaderHeight();
    // ONE pending backstop, restarted on every flip, so a missed transitionend
    // (tab hidden, iOS dropping it mid-scroll) cannot strand the value.
    if (pinnedSettleTimer) window.clearTimeout(pinnedSettleTimer);
    pinnedSettleTimer = window.setTimeout(function () {
      pinnedSettleTimer = null;
      publishPinnedHeaderHeight();
    }, PINNED_HEADER_SETTLE_MS);
  }

  function create(options) {
    var bar = options.bar;
    var trigger = options.trigger;
    if (!bar || !trigger) return null;

    var activeClass = options.activeClass || 'is-stuck';
    var freezeClass = options.freezeClass || DEFAULT_FREEZE_CLASS;
    var onToggle =
      typeof options.onToggle === 'function' ? options.onToggle : null;

    // Rule 3. The sentinel marks where the TRIGGER sits, so the bar itself is
    // free to be moved, fixed or transformed without corrupting the reading.
    var sentinel = document.createElement('div');
    sentinel.style.cssText =
      'height:0;display:block;visibility:hidden;pointer-events:none;overflow:hidden;';
    trigger.parentNode.insertBefore(sentinel, trigger);

    var headerHeight = 0;
    var triggerDocTop = 0;
    var isActive = false;
    var rafPending = false;

    function setActive(next) {
      // Rule 4.
      if (freezeClass && document.body.classList.contains(freezeClass)) return;
      if (next === isActive) return;
      isActive = next;
      bar.classList.toggle(activeClass, next);
      if (onToggle) onToggle(next, bar);
    }

    function evaluate() {
      rafPending = false;
      // Trigger's own height is included so the bar activates once the trigger
      // has fully cleared the header, not the instant its top edge touches it.
      setActive(
        window.scrollY + headerHeight >= triggerDocTop + trigger.offsetHeight
      );
    }

    function onScroll() {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(evaluate);
    }

    function remeasure() {
      headerHeight = measureHeaderHeight();
      triggerDocTop = getDocTop(sentinel);
      evaluate();
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', remeasure, { passive: true });
    // Images above the trigger change its document position after they load.
    window.addEventListener('load', remeasure);
    // The theme's header dispatches this when its own height changes.
    document.body.addEventListener('header:update', remeasure);

    remeasure();

    return {
      remeasure: remeasure,
      isActive: function () {
        return isActive;
      },
      destroy: function () {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', remeasure);
        window.removeEventListener('load', remeasure);
        document.body.removeEventListener('header:update', remeasure);
        if (sentinel.parentNode) sentinel.parentNode.removeChild(sentinel);
        bar.classList.remove(activeClass);
      },
    };
  }

  /** Declarative init so simple consumers need no JS of their own. */
  function initFromDom() {
    var bars = document.querySelectorAll('[data-sticky-activate]');
    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      if (bar.__mdStickyBound) continue;
      var triggerSel = bar.getAttribute('data-sticky-trigger');
      if (!triggerSel) continue;
      var trigger = document.querySelector(triggerSel);
      if (!trigger) continue;
      bar.__mdStickyBound = true;
      create({
        bar: bar,
        trigger: trigger,
        activeClass: bar.getAttribute('data-sticky-activate') || 'is-stuck',
        freezeClass:
          bar.getAttribute('data-sticky-freeze-class') || DEFAULT_FREEZE_CLASS,
      });
    }
  }

  function boot() {
    initFromDom();
    watchPinnedHeader();
  }

  window.MDStickyActivation = {
    create: create,
    init: initFromDom,
    // Exposed so the pinned-header contract can be asserted directly, and so a
    // consumer that moves the header itself can force a re-read.
    refreshPinnedHeader: publishPinnedHeaderHeight,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
